/**
 * Zapis wspólnych źródeł ciepła z panelu z kontrolą wersji (migr. 058, Bronek 2026-10-09).
 *
 * Panel wysyłał pełną tablicę źródeł ze swojego odczytu — źródło dodane w międzyczasie na
 * tablecie znikało (nieobecne w tablicy = DELETE), a parametry zapisane z tabletu wracały do stanu
 * z odczytu panelu. Teraz: źródła bez zmian jadą jako samo `{ id }` (serwer ich nie rusza), a
 * `heatSourcesBase` = wersje z odczytu — serwer kasuje tylko źródła z bazy i odrzuca zapis (409
 * HEAT_SOURCES_CHANGED), gdy usuwane źródło zmieniono gdzie indziej.
 */
import type { HeatSource } from '../types';

/**
 * `{ id: rev }` z odczytu. `undefined`, gdy któreś źródło nie ma wersji (backend sprzed 058) —
 * wtedy zapis idzie po staremu, bez bazy (z pustą bazą serwer niczego by nie usunął).
 */
export function heatSourcesBaseOf(sources: HeatSource[]): Record<string, number> | undefined {
  const base: Record<string, number> = {};
  for (const s of sources) {
    if (typeof s.rev !== 'number') return undefined;
    base[s.id] = s.rev;
  }
  return base;
}

/** Źródła bez zmian — samo `{ id }` (bramka #57: brak treści = serwer zostawia wiersz). */
export function untouchedHeatSources(sources: HeatSource[]): Array<Pick<HeatSource, 'id'>> {
  return sources.map(({ id }) => ({ id }));
}

/** Odpowiedź 409 „źródła zmieniono na innym urządzeniu". */
export function isHeatSourcesConflict(error: unknown): boolean {
  const e = error as { status?: number; code?: string } | null;
  return !!e && e.status === 409 && e.code === 'HEAT_SOURCES_CHANGED';
}

/**
 * PUT /buildings/:id dla edycji parametrów JEDNEGO wspólnego źródła z panelu (B8, 2026-10-19) — ta sama
 * ścieżka z kontrolą wersji co tablet (mobile services/buildingEditSync.ts):
 *  - w tablicy tylko edytowane źródło, z `params` = params z odczytu ⊕ zmiany admina (serwer podmienia
 *    params w całości, więc zmienione na tablecie pola nie mogą zostać nadpisane starą kopią — chroni to
 *    `heatSourcesBase`); `type`/`heatCharacter` pominięte = serwer je zostawia (COALESCE, #57),
 *  - `heatSourcesBase` = wersja TEGO źródła z odczytu: zmienione w międzyczasie (tablet) → 409
 *    HEAT_SOURCES_CHANGED; pozostałe źródła są spoza bazy, więc serwer ich nie rusza ani nie kasuje,
 *  - `writeId` jak w kolejce tabletu: ponowienie tej samej wysyłki nie zapisze jej drugi raz.
 * `null`, gdy źródło nie ma wersji (backend sprzed 058) — bez bazy serwer skasowałby pozostałe źródła,
 * więc takiego zapisu nie wysyłamy.
 */
export function heatSourceParamsPayload(
  source: HeatSource,
  changes: Record<string, unknown>,
  writeId: string,
): {
  heatSources: Array<Pick<HeatSource, 'id' | 'params'>>;
  heatSourcesBase: Record<string, number>;
  writeId: string;
} | null {
  if (typeof source.rev !== 'number') return null;
  return {
    heatSources: [{ id: source.id, params: { ...(source.params || {}), ...changes } }],
    heatSourcesBase: { [source.id]: source.rev },
    writeId,
  };
}

/** uuid v4 dla `writeId` (serwer wymaga uuid); fallback na getRandomValues, gdy brak randomUUID. */
export function newWriteId(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
